import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./components/ui/dialog";
import { Button } from "./components/ui/button";
import { Badge } from "./components/ui/badge";
import {
  Table,
  TableHeader,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "./components/ui/table";
import { Trash2, Download, ArrowRightLeft, Users, Loader2 } from "lucide-react";

const formatJoined = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const EventWaitlistDialog = ({
  isOpen,
  onClose,
  event,
  onAfterChange, // called after remove / convert so the parent can refresh counts and bookings
}) => {
  const [entries, setEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [busyAction, setBusyAction] = useState(null);

  const fetchEntries = async () => {
    if (!event?.id) return;
    setIsLoading(true);
    setEntries([]);
    try {
      const res = await axios.get(`events/${event.id}/waitlist`);
      setEntries(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load waiting list");
      setEntries([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) fetchEntries();
  }, [isOpen, event?.id]);

  const handleRemove = async (entry) => {
    if (!window.confirm(`Remove ${entry.user_name} from the waiting list?`)) return;
    setBusyId(entry.id);
    setBusyAction("remove");
    try {
      await axios.delete(`events/${event.id}/waitlist/${entry.id}`);
      toast.success("Removed from waiting list");
      setEntries((prev) => prev.filter((e) => e.id !== entry.id));
      onAfterChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove entry");
    } finally {
      setBusyId(null);
      setBusyAction(null);
    }
  };

  const handleConvert = async (entry) => {
    const summary = [
      `${entry.requested_tickets} ticket(s)`,
      entry.partner_included ? "+ partner" : null,
      entry.minor_count ? `+ ${entry.minor_count} minor(s)` : null,
    ].filter(Boolean).join(" ");
    if (
      !window.confirm(
        `Convert ${entry.user_name} (${summary}) into a confirmed booking?\n\nThe booking will be created as 'paid'. Collect payment manually before doing this.`
      )
    ) return;
    setBusyId(entry.id);
    setBusyAction("convert");
    try {
      const res = await axios.post(
        `events/${event.id}/waitlist/${entry.id}/convert`
      );
      toast.success(
        `Converted to booking — €${res.data.total_amount?.toFixed?.(2) ?? res.data.total_amount} for ${res.data.tickets_count} ticket(s)`
      );
      setEntries((prev) => prev.filter((e) => e.id !== entry.id));
      onAfterChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to convert entry");
    } finally {
      setBusyId(null);
      setBusyAction(null);
    }
  };

  const handleExport = async () => {
    try {
      const res = await axios.get(`events/${event.id}/waitlist/export`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `waitlist_${(event.title || "event").replace(/[^a-zA-Z0-9_-]/g, "_").slice(0, 40)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Waitlist exported");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to export waiting list");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className="max-w-5xl"
        data-testid="event-waitlist-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center justify-between gap-3">
            <span className="flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-700" />
              Waiting List — {event?.title || "Event"}
              <Badge
                variant="outline"
                className="bg-blue-50 border-blue-200 text-blue-700"
                data-testid="waitlist-total-count"
              >
                {entries.length}
              </Badge>
            </span>
            <Button
              size="sm"
              variant="outline"
              className="gap-2"
              onClick={handleExport}
              disabled={entries.length === 0 || isLoading}
              data-testid="waitlist-export-btn"
            >
              <Download className="h-4 w-4" />
              Export to Excel
            </Button>
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-2 py-3" data-testid="waitlist-loading-skeleton">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="animate-pulse h-12 rounded-md border border-slate-200 bg-slate-50"
              />
            ))}
          </div>
        ) : entries.length === 0 ? (
          <div
            className="py-10 text-center text-slate-500"
            data-testid="waitlist-empty"
          >
            <Users className="h-8 w-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm">No members are currently on the waiting list.</p>
          </div>
        ) : (
          <div className="overflow-x-auto" data-testid="waitlist-table-wrap">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">#</TableHead>
                  <TableHead>Member</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead className="text-center">Tickets</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((entry, idx) => (
                  <TableRow
                    key={entry.id}
                    data-testid={`waitlist-row-${entry.id}`}
                  >
                    <TableCell className="font-medium text-slate-500">
                      {idx + 1}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium text-slate-800">
                        {entry.user_name}
                      </div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {entry.partner_included && (
                          <Badge
                            variant="outline"
                            className="text-[10px] bg-purple-50 text-purple-700 border-purple-200"
                          >
                            + Partner
                          </Badge>
                        )}
                        {entry.minor_count > 0 && (
                          <Badge
                            variant="outline"
                            className="text-[10px] bg-amber-50 text-amber-700 border-amber-200"
                          >
                            + {entry.minor_count} minor{entry.minor_count > 1 ? "s" : ""}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs">
                      <div className="text-slate-700">{entry.user_email}</div>
                      {entry.user_mobile && (
                        <div className="text-slate-500">{entry.user_mobile}</div>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge
                        variant="secondary"
                        className="bg-slate-100 text-slate-700"
                      >
                        {entry.requested_tickets}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">
                      {formatJoined(entry.joined_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 gap-1 text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                          onClick={() => handleConvert(entry)}
                          disabled={busyId === entry.id}
                          data-testid={`waitlist-convert-${entry.id}`}
                          title="Convert this waitlist entry into a confirmed booking"
                        >
                          {busyId === entry.id && busyAction === "convert" ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <ArrowRightLeft className="h-3.5 w-3.5" />
                          )}
                          <span className="hidden sm:inline">Convert</span>
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 gap-1 text-red-700 border-red-200 hover:bg-red-50"
                          onClick={() => handleRemove(entry)}
                          disabled={busyId === entry.id}
                          data-testid={`waitlist-remove-${entry.id}`}
                          title="Remove from waiting list"
                        >
                          {busyId === entry.id && busyAction === "remove" ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Trash2 className="h-3.5 w-3.5" />
                          )}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <p className="mt-3 text-xs text-slate-500">
              <strong>Convert</strong> creates a confirmed (paid) booking from the waitlist entry — use after collecting payment manually.
              <strong> Remove</strong> deletes the entry without notifying the member.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EventWaitlistDialog;
